import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, RefreshCw, Settings2 } from "lucide-react";
import toast from "react-hot-toast";
import { salaryStructureService, flexiBenefitsService } from "../../../../services/api";
import { usePermissions } from "../../../../rbac/PermissionsContext";
import { DsButton, DsIcon, DsInput } from "../../../../design-system";

/**
 * TDS Reference Head Master — system income-tax classification heads.
 * Editable only by Super Admin; all admins can view for component mapping.
 */
export default function TdsReferenceHeadsManager({ darkMode = false }) {
  const { role, hasScreenAccess, isAdmin } = usePermissions() || {};
  const isSuperAdmin = role === "super_admin";
  const canView =
    isAdmin ||
    isSuperAdmin ||
    (typeof hasScreenAccess === "function" &&
      (hasScreenAccess("payroll", "tds_reference_heads", "view") ||
        hasScreenAccess("payroll", "salary_structure", "view")));
  /** Spec: editable only by Super Admin */
  const canEdit = isSuperAdmin;
  const [loading, setLoading] = useState(true);
  const [heads, setHeads] = useState([]);
  const [groupFilter, setGroupFilter] = useState("ALL");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configuring, setConfiguring] = useState(null);
  const [ruleVersions, setRuleVersions] = useState([]);
  const [ruleSaving, setRuleSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await salaryStructureService.listTdsReferenceHeads({
        includeInactive: isSuperAdmin ? "1" : undefined,
      });
      setHeads(res?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load TDS Reference Heads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (groupFilter === "ALL") return heads;
    return heads.filter((h) => h.headGroup === groupFilter);
  }, [heads, groupFilter]);

  const border = "border-[hsl(var(--ds-border))]";
  const muted = "text-[hsl(var(--ds-muted-fg))]";

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!canEdit || !editing) return;
    setSaving(true);
    try {
      const form = e.target;
      await salaryStructureService.updateTdsReferenceHead(editing.id, {
        name: form.headName.value.trim(),
        taxability: form.taxability.value,
        incomeTaxSection: form.incomeTaxSection.value.trim() || null,
        exemptionSection: form.exemptionSection.value.trim() || null,
        deductionSection: form.deductionSection.value.trim() || null,
        oldRegime: form.oldRegime.checked,
        newRegime: form.newRegime.checked,
        maxAnnualLimit: form.maxAnnualLimit.value
          ? Number(form.maxAnnualLimit.value)
          : null,
        proofRequired: form.proofRequired.checked,
        includeInGross: form.includeInGross.checked,
        includeInTaxable: form.includeInTaxable.checked,
        includeInForm16: form.includeInForm16.checked,
        active: form.active.checked,
      });
      toast.success("TDS Reference Head updated");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const saveCreate = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    try {
      const form = e.target;
      await salaryStructureService.createTdsReferenceHead({
        code: form.headCode.value.trim().toUpperCase().replace(/\s+/g, "_"),
        name: form.headName.value.trim(),
        headGroup: form.headGroup.value,
        taxability: form.taxability.value,
        deductionSection: form.deductionSection.value.trim() || null,
        exemptionSection: form.exemptionSection.value.trim() || null,
        oldRegime: true,
        newRegime: true,
      });
      toast.success("TDS Reference Head created");
      setCreating(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return (
      <p className={`py-6 text-sm ${muted}`}>
        You do not have access to view TDS Reference Heads.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">TDS Reference Heads</h3>
          <p className={`ds-caption ${muted}`}>
            Standard income-tax classification for salary components. Tax engine uses these heads —
            not component names. {canEdit ? "Super Admin can edit." : "View only (Super Admin edits)."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className={`h-8 rounded-md border px-2 text-xs ${border} bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]`}
          >
            <option value="ALL">All groups</option>
            <option value="EARNING">Earnings</option>
            <option value="DEDUCTION">Deductions</option>
            <option value="CHAPTER_VIA">Chapter VI-A</option>
          </select>
          <DsButton type="button" size="sm" variant="outline" onClick={load} title="Refresh">
            <DsIcon icon={RefreshCw} size="sm" />
          </DsButton>
          {canEdit && (
            <DsButton type="button" size="sm" variant="primary" onClick={() => setCreating(true)}>
              <DsIcon icon={Plus} size="sm" /> Add head
            </DsButton>
          )}
        </div>
      </div>

      {loading ? (
        <div className={`flex items-center gap-2 py-8 text-sm ${muted}`}>
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="ds-table-wrap">
          <table className="ds-table min-w-[720px] text-xs">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Group</th>
                <th>Taxability</th>
                <th>Section</th>
                <th>Regime</th>
                <th>Ver</th>
                {canEdit && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((h) => (
                <tr key={h.id}>
                  <td className="font-mono text-[10px]">{h.code}</td>
                  <td>{h.name}</td>
                  <td>{h.headGroup}</td>
                  <td>{h.taxability}</td>
                  <td className={muted}>
                    {h.deductionSection || h.exemptionSection || h.incomeTaxSection || "—"}
                  </td>
                  <td>
                    {[h.oldRegime ? "OLD" : null, h.newRegime ? "NEW" : null]
                      .filter(Boolean)
                      .join("/") || "—"}
                  </td>
                  <td className="tabular-nums">{h.version}</td>
                  {canEdit && (
                    <td className="text-right">
                      <button
                        type="button"
                        onClick={async () => {
                          setConfiguring(h);
                          try {
                            const res = await flexiBenefitsService.listHeadRuleVersions(h.id);
                            setRuleVersions(res?.data || []);
                          } catch {
                            setRuleVersions([]);
                          }
                        }}
                        className="mr-1 rounded p-1 hover:bg-[hsl(var(--ds-muted))]"
                        title="Configure tax rule (versioned)"
                      >
                        <Settings2 size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(h)}
                        className="rounded p-1 hover:bg-[hsl(var(--ds-muted))]"
                        title="Edit head"
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {configuring && canEdit && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setRuleSaving(true);
              try {
                const form = e.target;
                const code = String(configuring.code || "").toUpperCase();
                let ruleJson = {
                  calcType:
                    code === "HRA"
                      ? "HRA_MIN_OF_THREE"
                      : code === "LTA"
                        ? "LTA_RULE_2B"
                        : "GENERIC",
                  section: form.section.value.trim() || configuring.exemptionSection,
                  oldRegime: form.oldRegime.checked,
                  newRegime: form.newRegime.checked,
                  treatment: form.treatment.value,
                  proofRequired: form.proofRequired.checked,
                  maxAnnualLimit: form.maxAnnualLimit.value
                    ? Number(form.maxAnnualLimit.value)
                    : null,
                };
                if (code === "HRA") {
                  ruleJson = {
                    ...ruleJson,
                    calcType: "HRA_MIN_OF_THREE",
                    operands: {
                      actualHra: { source: "component_annual" },
                      rentMinusPctOfSalary: {
                        pctOfSalary: Number(form.rentPct.value) || 10,
                        salaryBasis: "BASIC_DA",
                      },
                      pctOfSalaryByCity: {
                        metroPct: Number(form.metroPct.value) || 50,
                        nonMetroPct: Number(form.nonMetroPct.value) || 40,
                        salaryBasis: "BASIC_DA",
                      },
                    },
                    aggregate: "MIN",
                  };
                }
                await flexiBenefitsService.publishHeadRuleVersion(configuring.id, {
                  ruleJson,
                  effectiveFrom: form.effectiveFrom.value,
                  effectiveTo: form.effectiveTo.value || null,
                  notes: form.notes.value.trim() || null,
                  status: "ACTIVE",
                });
                toast.success("New tax rule version saved (history preserved)");
                setConfiguring(null);
              } catch (err) {
                toast.error(err?.response?.data?.message || "Failed to save rule version");
              } finally {
                setRuleSaving(false);
              }
            }}
            className={`w-full max-w-lg space-y-3 rounded-xl border p-4 shadow-[var(--ds-shadow-card)] ${border} bg-[hsl(var(--ds-surface))]`}
          >
            <h4 className="ds-h4 text-[hsl(var(--ds-fg))]">
              Static IT rule · {configuring.code} (new version)
            </h4>
            <p className={`ds-caption ${muted}`}>
              This is the <strong>statutory (static)</strong> layer — Act / Rules / CBDT. Company
              Flexi ceilings stay on the salary component. Does not overwrite historical rules;
              prior ACTIVE versions are superseded from the day before Effective From.
            </p>
            {ruleVersions.length > 0 && (
              <p className={`text-[11px] ${muted}`}>
                Existing versions:{" "}
                {ruleVersions
                  .slice(0, 5)
                  .map((v) => `v${v.version} (${v.status})`)
                  .join(", ")}
              </p>
            )}
            <DsInput name="section" defaultValue={configuring.exemptionSection || ""} placeholder="Section" darkMode={darkMode} />
            <select
              name="treatment"
              defaultValue={configuring.taxability || "TAXABLE"}
              className={`h-9 w-full rounded-md border px-3 text-sm ${border} bg-[hsl(var(--ds-surface))]`}
            >
              <option value="TAXABLE">TAXABLE</option>
              <option value="FULLY_EXEMPT">FULLY_EXEMPT</option>
              <option value="PARTIALLY_EXEMPT">PARTIALLY_EXEMPT</option>
            </select>
            {String(configuring.code).toUpperCase() === "HRA" && (
              <div className="grid grid-cols-3 gap-2">
                <DsInput name="rentPct" type="number" defaultValue={10} placeholder="Rent − % salary" darkMode={darkMode} />
                <DsInput name="metroPct" type="number" defaultValue={50} placeholder="Metro %" darkMode={darkMode} />
                <DsInput name="nonMetroPct" type="number" defaultValue={40} placeholder="Non-metro %" darkMode={darkMode} />
              </div>
            )}
            <DsInput name="maxAnnualLimit" type="number" defaultValue={configuring.maxAnnualLimit ?? ""} placeholder="Max annual" darkMode={darkMode} />
            <div className="grid grid-cols-2 gap-2">
              <DsInput name="effectiveFrom" type="date" defaultValue="2026-04-01" required darkMode={darkMode} />
              <DsInput name="effectiveTo" type="date" darkMode={darkMode} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className="flex items-center gap-2">
                <input name="oldRegime" type="checkbox" defaultChecked={!!configuring.oldRegime} /> OLD
              </label>
              <label className="flex items-center gap-2">
                <input name="newRegime" type="checkbox" defaultChecked={!!configuring.newRegime} /> NEW
              </label>
              <label className="flex items-center gap-2">
                <input name="proofRequired" type="checkbox" defaultChecked={!!configuring.proofRequired} /> Proof required
              </label>
            </div>
            <DsInput name="notes" placeholder="Version notes" darkMode={darkMode} />
            <div className="flex justify-end gap-2 pt-2">
              <DsButton type="button" size="sm" variant="secondary" onClick={() => setConfiguring(null)}>
                Cancel
              </DsButton>
              <DsButton type="submit" size="sm" variant="primary" loading={ruleSaving} disabled={ruleSaving}>
                Save as new version
              </DsButton>
            </div>
          </form>
        </div>
      )}

      {(editing || creating) && canEdit && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={editing ? saveEdit : saveCreate}
            className={`w-full max-w-lg space-y-3 rounded-xl border p-4 shadow-[var(--ds-shadow-card)] ${border} bg-[hsl(var(--ds-surface))]`}
          >
            <h4 className="ds-h4 text-[hsl(var(--ds-fg))]">
              {editing ? `Edit · ${editing.code}` : "New TDS Reference Head"}
            </h4>
            {creating && (
              <>
                <DsInput
                  name="headCode"
                  required
                  placeholder="CODE"
                  darkMode={darkMode}
                />
                <select
                  name="headGroup"
                  className={`h-9 w-full rounded-md border px-3 text-sm ${border} bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]`}
                  defaultValue="EARNING"
                >
                  <option value="EARNING">EARNING</option>
                  <option value="DEDUCTION">DEDUCTION</option>
                  <option value="CHAPTER_VIA">CHAPTER_VIA</option>
                </select>
              </>
            )}
            <DsInput
              name="headName"
              required
              defaultValue={editing?.name || ""}
              placeholder="Display name"
              darkMode={darkMode}
            />
            <select
              name="taxability"
              defaultValue={editing?.taxability || "TAXABLE"}
              className={`h-9 w-full rounded-md border px-3 text-sm ${border} bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]`}
            >
              <option value="TAXABLE">TAXABLE</option>
              <option value="FULLY_EXEMPT">FULLY_EXEMPT</option>
              <option value="PARTIALLY_EXEMPT">PARTIALLY_EXEMPT</option>
            </select>
            {editing && (
              <>
                <DsInput
                  name="incomeTaxSection"
                  defaultValue={editing.incomeTaxSection || ""}
                  placeholder="Income tax section"
                  darkMode={darkMode}
                />
                <DsInput
                  name="exemptionSection"
                  defaultValue={editing.exemptionSection || ""}
                  placeholder="Exemption section"
                  darkMode={darkMode}
                />
              </>
            )}
            <DsInput
              name="deductionSection"
              defaultValue={editing?.deductionSection || ""}
              placeholder="Deduction section (e.g. 80C, 16(iii))"
              darkMode={darkMode}
            />
            {editing && (
              <>
                <DsInput
                  name="maxAnnualLimit"
                  type="number"
                  defaultValue={editing.maxAnnualLimit ?? ""}
                  placeholder="Max annual limit"
                  darkMode={darkMode}
                />
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    ["oldRegime", "OLD regime", editing.oldRegime],
                    ["newRegime", "NEW regime", editing.newRegime],
                    ["proofRequired", "Proof required", editing.proofRequired],
                    ["includeInGross", "Include in gross", editing.includeInGross],
                    ["includeInTaxable", "Include in taxable", editing.includeInTaxable],
                    ["includeInForm16", "Include in Form-16", editing.includeInForm16],
                    ["active", "Active", editing.active],
                  ].map(([name, label, def]) => (
                    <label key={name} className="flex items-center gap-2 text-[hsl(var(--ds-fg))]">
                      <input name={name} type="checkbox" defaultChecked={!!def} />
                      {label}
                    </label>
                  ))}
                </div>
              </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <DsButton
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setEditing(null);
                  setCreating(false);
                }}
              >
                Cancel
              </DsButton>
              <DsButton type="submit" size="sm" variant="primary" loading={saving} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </DsButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
